import React, { FunctionComponent } from 'react';

import { isNull, isUndefined, map } from 'lodash';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import 'common/upload/uploader/error-modal/ErrorModal.scss';

export enum ErrorCode {
    FileTooSmall = 'file-too-small',
    FileTooLarge = 'file-too-large',
    TooManyFiles = 'too-many-files',
    FileInvalidType = 'file-invalid-type',
    TotalFilesTooLarge = 'total-files-too-large',
    Default = 'unable-to-upload',
}

export interface InvalidFileError {
    code?: string;
    message?: string;
}

export interface InvalidFile {
    file: File;
    errors: InvalidFileError[];
}

interface HideFunc {
    (): void;
}

export interface Props {
    show: boolean;
    accept?: string;
    maxFiles?: number;
    minSizePerFile?: number | null;
    maxSizePerFile?: number | null;
    maxSizeTotal?: number | null;
    totalUploadedFilesSize?: number | null;
    rejectedFiles: InvalidFile[];
    onHide: HideFunc;
}

export const ErrorModal: FunctionComponent<Props> = ({
    show,
    onHide,
    accept,
    maxFiles,
    minSizePerFile,
    maxSizePerFile,
    maxSizeTotal,
    totalUploadedFilesSize,
    rejectedFiles,
}): JSX.Element => {
    const { t } = useTranslation(['common/upload']);

    const bytesToSize = (bytes: number | null | undefined): string => {
        if (!bytes) {
            return '0 Byte';
        }
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1000)).toString(), 10);

        return `${(bytes / 1000 ** i).toFixed(2)}  ${sizes[i]}`;
    };

    const getErrorMessage = (errorItem: InvalidFileError): string => {
        let message = '';
        let value = null;

        if (errorItem) {
            switch (errorItem.code) {
                case ErrorCode.FileTooSmall:
                    message = 'INVALID_MIN_FILE_SIZE';
                    value = bytesToSize(minSizePerFile!);
                    break;
                case ErrorCode.FileTooLarge:
                    message = 'INVALID_MAX_FILE_SIZE';
                    value = bytesToSize(maxSizePerFile!);
                    break;
                case ErrorCode.TooManyFiles:
                    message = 'INVALID_FILES_COUNT';
                    value = maxFiles;
                    break;
                case ErrorCode.FileInvalidType:
                    message = 'INVALID_FILE_TYPE';
                    break;
                case ErrorCode.TotalFilesTooLarge:
                    message = 'INVALID_TOTAL_FILE_SIZE';
                    value = bytesToSize(maxSizeTotal!);
                    break;
                case ErrorCode.Default:
                    message = 'UNABLE_UPLOAD_FILE';
                    break;
                default:
                    if (errorItem.message) {
                        message = errorItem.message;
                    } else {
                        message = 'UNABLE_UPLOAD_FILE';
                    }
                    break;
            }
        }
        return t(message, { value });
    };

    return (
        <Modal contentClassName="r4e-uploader-error-modal" show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <Modal.Title>
                    <div>{t('UNABLE_ATTACH_FILES', { count: rejectedFiles.length })}</div>
                    <div className="threshold">
                        {accept ? (
                            <div>
                                {t('ACCEPTED_FORMATS', { formats: accept, interpolation: { escapeValue: false } })}
                            </div>
                        ) : null}
                        {!isNull(maxFiles) ? <div>{t('MAX_FILES', { limit: maxFiles })}</div> : null}
                        {!isNull(minSizePerFile) ? (
                            <div>{t('MIN_SIZE_PER_FILE', { limit: bytesToSize(minSizePerFile) })}</div>
                        ) : null}
                        {!isUndefined(maxSizePerFile) ? (
                            <div>{t('MAX_SIZE_PER_FILE', { limit: bytesToSize(maxSizePerFile) })}</div>
                        ) : null}
                        {!isNull(maxSizeTotal) ? (
                            <div>{t('TOTAL_SIZE_LIMIT', { limit: bytesToSize(maxSizeTotal) })}</div>
                        ) : null}
                        {!isNull(totalUploadedFilesSize) ? (
                            <div>{t('UPLOADED_FILES_SIZE', { size: bytesToSize(totalUploadedFilesSize) })}</div>
                        ) : null}
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {map(rejectedFiles, (fileItem: InvalidFile, fileKey: string) => (
                    <article key={fileKey} className="rejected-file">
                        <div>
                            <span className="file-name">{fileItem.file.name}</span>
                            <span className="file-size">{bytesToSize(fileItem.file.size)}</span>
                        </div>
                        <div>
                            {map(fileItem.errors, (errorItem: InvalidFileError, errorKey: string) => (
                                <div key={errorKey} className="error-container">
                                    <i className="icon-exclamation-circle" />
                                    <span className="file-error">{getErrorMessage(errorItem)}</span>
                                </div>
                            ))}
                        </div>
                    </article>
                ))}
            </Modal.Body>
        </Modal>
    );
};
